import { useState, useEffect } from 'react'
import { Users, UserPlus, Trash2, X, Loader2, Copy, Check, Mail } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useTenant } from '../../hooks/useTenant'
import {
  listTenantUsers,
  setTenantUserRole,
  removeTenantUser,
  TenantUser,
} from '../../services/api'
import toast from 'react-hot-toast'

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-red-100 text-red-700',
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  editor: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-600',
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  super_admin: 'Platform owner. Full access to all companies and settings.',
  owner: 'Full access. Manages company settings, team, and billing.',
  admin: 'Full operational access. Can manage team members and settings.',
  editor: 'Can handle tickets, manage agents, and edit knowledge base.',
  viewer: 'Read-only access to dashboard, inbox, and analytics.',
}

function formatRole(role: string): string {
  if (role === 'super_admin') return 'Super Admin'
  return role.charAt(0).toUpperCase() + role.slice(1)
}

export default function TeamPage() {
  const { user, isSuperAdmin } = useAuth()
  const { tenantId } = useTenant()
  const [users, setUsers] = useState<TenantUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('editor')
  const [inviting, setInviting] = useState(false)
  const [copiedLink, setCopiedLink] = useState(false)
  const [lastInvitedEmail, setLastInvitedEmail] = useState('')

  const currentUserEmail = user?.email?.toLowerCase() || ''
  const currentUserEntry = users.find((u) => u.email.toLowerCase() === currentUserEmail)
  const currentUserRole = isSuperAdmin ? 'super_admin' : (currentUserEntry?.role || 'viewer')
  const canManageTeam = isSuperAdmin || ['owner', 'admin'].includes(currentUserRole)

  useEffect(() => {
    if (tenantId) loadUsers()
  }, [tenantId])

  async function loadUsers() {
    if (!tenantId) return
    try {
      setLoading(true)
      const data = await listTenantUsers(tenantId)
      setUsers(data.users || [])
    } catch (err) {
      console.error('Failed to load team:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleInvite() {
    if (!inviteEmail.trim() || !tenantId) return
    setInviting(true)
    try {
      await setTenantUserRole(tenantId, inviteEmail.trim(), inviteRole)
      setLastInvitedEmail(inviteEmail.trim())
      setInviteEmail('')
      setShowInviteForm(false)
      toast.success(`Invited ${inviteEmail.trim()} as ${inviteRole}`)
      await loadUsers()
    } catch (err: any) {
      toast.error(err.message || 'Failed to invite user')
    } finally {
      setInviting(false)
    }
  }

  async function handleChangeRole(email: string, role: string) {
    if (!tenantId) return
    try {
      await setTenantUserRole(tenantId, email, role)
      toast.success(`Updated ${email} role to ${role}`)
      await loadUsers()
    } catch (err) {
      toast.error('Failed to update role')
    }
  }

  async function handleRemoveUser(email: string) {
    if (!tenantId || !confirm(`Remove ${email} from the team?`)) return
    try {
      await removeTenantUser(tenantId, email)
      toast.success(`Removed ${email} from the team`)
      await loadUsers()
    } catch (err) {
      toast.error('Failed to remove user')
    }
  }

  function handleCopyInviteLink() {
    const link = `${window.location.origin}/register`
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(true)
      toast.success('Invite link copied!')
      setTimeout(() => setCopiedLink(false), 2000)
    })
  }

  function getInitials(email: string): string {
    const name = email.split('@')[0]
    return name.slice(0, 2).toUpperCase()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-gray-300 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          </div>
          <p className="text-gray-500 mt-1 text-sm">
            Manage your team members and their access levels
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Your Role */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100">
            <span className="text-xs text-gray-500">Your role:</span>
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase ${ROLE_COLORS[currentUserRole] || ROLE_COLORS.viewer}`}>
              {formatRole(currentUserRole)}
            </span>
          </div>
          {/* Invite Button */}
          {canManageTeam && (
            <button
              onClick={() => setShowInviteForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Invite Member
            </button>
          )}
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && canManageTeam && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Invite a Team Member</h3>
            </div>
            <button
              onClick={() => { setShowInviteForm(false); setInviteEmail('') }}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                placeholder="colleague@company.com"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white"
              >
                <option value="admin">Admin</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <button
              onClick={handleInvite}
              disabled={!inviteEmail.trim() || inviting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-40"
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Send Invite
            </button>
          </div>
          <p className="mt-2 text-xs text-blue-600">
            The invited user will need to register at ReplyForge with this email to access the team.
          </p>
        </div>
      )}

      {/* Copy Invite Link (shown after invitation) */}
      {lastInvitedEmail && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-800">
              <strong>{lastInvitedEmail}</strong> has been invited. Share this link for them to register:
            </span>
          </div>
          <button
            onClick={handleCopyInviteLink}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
          >
            {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copiedLink ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      )}

      {/* Role Legend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Role Permissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.entries(ROLE_DESCRIPTIONS).map(([role, desc]) => (
            <div key={role} className="flex items-start gap-2 p-2 rounded-lg">
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase mt-0.5 ${ROLE_COLORS[role]}`}>
                {role}
              </span>
              <p className="text-xs text-gray-500">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Team Members Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            Team Members ({users.length})
          </h3>
        </div>

        {users.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            No team members yet. Invite someone to get started.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Member</th>
                <th className="text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Role</th>
                {canManageTeam && (
                  <th className="text-right text-[10px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3 w-20">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map((u) => {
                const isCurrentUser = u.email.toLowerCase() === currentUserEmail
                return (
                  <tr
                    key={u.email}
                    className={`${isCurrentUser ? 'bg-amber-50/30' : 'hover:bg-gray-50/50'}`}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-gray-600">{getInitials(u.email)}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {u.email}
                            {isCurrentUser && (
                              <span className="ml-2 text-xs text-amber-600 font-normal">(you)</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {['owner', 'super_admin'].includes(u.role) || !canManageTeam ? (
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>
                          {u.role}
                        </span>
                      ) : (
                        <select
                          value={u.role}
                          onChange={(e) => handleChangeRole(u.email, e.target.value)}
                          className={`px-2 py-0.5 text-xs font-medium rounded-full border-0 outline-none cursor-pointer ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}
                        >
                          <option value="admin">Admin</option>
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      )}
                    </td>
                    {canManageTeam && (
                      <td className="px-5 py-3 text-right">
                        {!['owner', 'super_admin'].includes(u.role) && !isCurrentUser && (
                          <button
                            onClick={() => handleRemoveUser(u.email)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove from team"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
